import { styled } from 'styled-components';

export const palette = {
  default: '#445672',
  navy: '#1677e5',
  ink: '#0f172a',
  muted: '#64748b',
  label: '#6b7280',
  border: '#e5e7eb',
  barBg: '#eef2ff',
  barText: '#334155',
  icon: '#64748b',
  iconBg: '#eef2ff',
  matched: '#16a34a',
  matchedBg: '#dcfce7',
  warning: '#ea580c',
  warningBg: '#ffedd5',
  danger: '#dc2626',
  dangerBg: '#fee2e2',
  notesBg: '#eef2ff',
  notesBorder: '#c7d2fe',
  notesTitle: '#1e3a8a',
  statusOnSite: '#16a34a',
  statusAwaiting: '#2563eb',
  statusNotIn: '#9ca3af',
  statusTransit: '#0891b2',
};

export type MetricTone = 'default' | 'navy' | 'matched' | 'warning' | 'danger';

export const metricTone: Record<
  MetricTone,
  { value: string; icon: string; iconBg: string }
> = {
  default: {
    value: palette.default,
    icon: palette.icon,
    iconBg: palette.iconBg,
  },
  navy: {
    value: palette.navy,
    icon: palette.navy,
    iconBg: '#eef5fd',
  },
  matched: {
    value: palette.matched,
    icon: palette.matched,
    iconBg: palette.matchedBg,
  },
  warning: {
    value: palette.warning,
    icon: palette.warning,
    iconBg: palette.warningBg,
  },
  danger: {
    value: palette.danger,
    icon: palette.danger,
    iconBg: palette.dangerBg,
  },
};

export const SelectedFile = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  margin-top: 8px;
  color: ${palette.muted};
`;

export const PageHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
`;

export const Breadcrumb = styled.div`
  color: ${palette.muted};
  font-size: 13px;
`;

export const BreadcrumbLink = styled.button`
  border: 0;
  background: none;
  padding: 0;
  color: ${({ theme }) => theme.accent};
  cursor: pointer;

  &:hover {
    text-decoration: underline;
  }
`;

export const PageTitle = styled.h1`
  font-size: 28px;
  font-weight: 700;
  line-height: 1.2;
  margin: 0;
  color: ${palette.ink};
`;

export const PageSubtitle = styled.p`
  margin-top: 8px;
  color: ${palette.muted};
`;

export const ReconstructionBar = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  margin-top: 10px;
  color: ${palette.barText};
  font-size: 13px;
  background: ${palette.barBg};
  border-radius: 8px;
  padding: 12px 16px;

  .ant-divider-vertical {
    border-inline-start-color: ${palette.default};
    height: 1em;
    top: 0;
  }
`;

export const ReconstructionLabel = styled.span`
  color: ${palette.muted};
`;

export const ReconstructionValue = styled.span`
  color: ${palette.ink};
  font-weight: 600;
`;

export const KpiGrid = styled.div`
  display: flex;
  gap: 16px;
  align-items: stretch;

  @media (max-width: 1199px) {
    flex-direction: column;
  }
`;

export const KpiMetrics = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: 20px;
  min-width: 0;
`;

export const SummaryGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 16px;

  @media (max-width: 767px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
`;

export const DiscrepancyGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 16px;

  @media (max-width: 767px) {
    grid-template-columns: 1fr;
  }
`;

export const MetricCard = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 12px;
  padding: 16px 18px;
  border: 1px solid ${palette.border};
  border-radius: 8px;
  background: #fff;
`;

export const MetricIcon = styled.div<{ $tone?: MetricTone }>`
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 8px;
  background: ${({ $tone = 'default' }) => metricTone[$tone].iconBg};
  color: ${({ $tone = 'default' }) => metricTone[$tone].icon};
  font-size: 18px;
  line-height: 1;
`;

export const MetricBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
`;

export const MetricLabel = styled.div`
  color: ${palette.label};
  font-size: 13px;
  line-height: 1.2;
`;

export const MetricValue = styled.div<{ $tone?: MetricTone }>`
  font-size: 28px;
  font-weight: 700;
  line-height: 1.1;
  color: ${({ $tone = 'default' }) => metricTone[$tone].value};
`;

export const NotesCard = styled(MetricCard)`
  width: 460px;
  flex: 0 0 460px;
  height: auto;
  flex-direction: column;
  align-items: flex-start;

  @media (max-width: 1199px) {
    width: 100%;
    flex: 1 1 auto;
  }
`;

export const NotesTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: 600;
  margin-bottom: 4px;
  color: ${palette.notesTitle};
`;

export const NotesList = styled.ul`
  margin: 0;
  padding-left: 18px;
  color: ${palette.barText};
  font-size: 13px;
  line-height: 1.6;
`;

export const ResultCell = styled.span<{ $color?: string }>`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  white-space: nowrap;
  color: ${({ $color }) => $color || palette.ink};
  font-weight: 600;
`;

export const StatusCell = styled.span<{ $color?: string }>`
  color: ${({ $color }) => $color || palette.ink};
  font-weight: 600;
`;

export const Filters = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 12px;
`;

export const TableToolbar = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
  margin-bottom: 12px;
`;

export const EmptyCell = styled.span`
  color: ${palette.muted};
  opacity: 0.7;
`;
